import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import KidBackground from '../ui/backgrounds/KidBackground';
import { addDoc, collection, serverTimestamp, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { CONSENT_MAPPING } from '../components/signup/consentQuestionsSimple';
import SimpleConsentForm from '../components/signup/SimpleConsentForm';
import { saveConsentSettings } from '../components/signup/ConsentService';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';
import { useAuth } from '../context/AuthContext';
import splashImage from '../assets/splash.png';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { user } = useAuth();
  const initial = state?.items || state?.selectedBooks || [];
  const selectedPlanId = state?.plan || state?.selectedPlan || null; // 'base' | 'pro' | 'premium' | legacy
  console.log('CheckoutPage - selectedPlanId:', selectedPlanId, 'state:', state);
  console.log('CheckoutPage - localStorage selectedPlan:', localStorage.getItem('selectedPlan'));
  
  // Force single mentor count for single mentor plans
  const isSingleMentor = selectedPlanId === 'mentor-basic' || selectedPlanId === 'mentor-pro' || selectedPlanId === 'mentor-premium';
  const mentorCount = isSingleMentor ? 1 : (state?.mentorCount || 1);
  console.log('CheckoutPage - isSingleMentor:', isSingleMentor, 'mentorCount:', mentorCount);
  
  // If no plan is received, try to get it from localStorage as fallback
  const fallbackPlanId = localStorage.getItem('selectedPlan') || null;
  const finalPlanId = selectedPlanId || fallbackPlanId || 'mentor-basic'; // Default to mentor-basic if no plan found
  console.log('CheckoutPage - finalPlanId:', finalPlanId, 'fallbackPlanId:', fallbackPlanId);
  console.log('CheckoutPage - selectedPlanId from state:', selectedPlanId);
  console.log('CheckoutPage - state object:', state);
  console.log('CheckoutPage - localStorage selectedPlan:', localStorage.getItem('selectedPlan'));
  console.log('CheckoutPage - All localStorage keys:', Object.keys(localStorage));
  console.log('CheckoutPage - user object:', user);
  console.log('CheckoutPage - location object:', location);
  const displayPlanName = (() => {
    console.log('CheckoutPage - calculating display name for finalPlanId:', finalPlanId);
    if (!finalPlanId) return null;
    if (finalPlanId === 'base') return 'Base';
    if (finalPlanId === 'pro') return 'Pro';
    if (finalPlanId === 'premium') return 'Premium';
    if (finalPlanId === 'mentor-basic') return 'Mentor Basic';
    if (finalPlanId === 'mentor-pro') return 'Mentor Pro';
    if (finalPlanId === 'mentor-premium') return 'Mentor Premium';
    if (finalPlanId === 'monthly') return 'Monthly';
    if (finalPlanId === 'semiannual') return '6 Months';
    if (finalPlanId === 'annual') return 'Yearly';
    console.log('CheckoutPage - using fallback name for finalPlanId:', finalPlanId);
    return String(finalPlanId);
  })();
  
  // Initialize items with quantity property
  const initialItemsWithQuantity = initial.map(item => ({
    ...item,
    quantity: item.quantity || 1
  }));
  
  const [items, setItems] = useState(initialItemsWithQuantity);
  const [printMode] = useState(state?.printMode || false);
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState({
    name: '', email: '', phone: '',
    line1: '', line2: '', city: '', state: '', postalCode: '', country: 'India'
  });
  const [agreed, setAgreed] = useState(false);
  // Consent state
  const [showConsent, setShowConsent] = useState(false);
  const [consent, setConsent] = useState(CONSENT_MAPPING); // Use simplified consent mapping
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const isUnder13 = (() => {
    try { 
      const stored = localStorage.getItem('signup_isUnder13');
      const result = stored === 'true';
      console.log('🔍 CheckoutPage - isUnder13 check:', { 
        stored, 
        result, 
        allLocalStorage: {
          accountType: localStorage.getItem('signup_accountType'),
          isUnder13: localStorage.getItem('signup_isUnder13'),
          parentEmail: localStorage.getItem('signup_parentEmail')
        }
      });
      return result;
    } catch (_) { 
      return false; 
    }
  })();
  const parentEmailFromSignup = (() => { try { return localStorage.getItem('signup_parentEmail') || ''; } catch(_) { return ''; } })();

  const handleConsentAgree = async (consentData) => {
    console.log('🔍 Confirming consent:', { consentData, isUnder13, parentEmailFromSignup });
    const userId = auth?.currentUser?.uid || 'current-user';
    
    // Save consent settings to consentSettings collection
    await saveConsentSettings({ 
      userId, 
      accountType: isUnder13 ? 'student' : 'parent', 
      consent: consentData, 
      parentEmail: parentEmailFromSignup || null,
      consentConfirmed: true
    });
    
    // Also apply consent directly to user document for immediate access
    if (isUnder13 && userId && userId !== 'current-user') {
      try {
        const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        await updateDoc(doc(db, 'users', userId), {
          consent: consentData,
          consentConfirmed: true,
          consentAppliedAt: serverTimestamp()
        });
        console.log('✅ Consent settings applied to user document');
      } catch (e) {
        console.error('❌ Error applying consent to user document:', e.message);
      }
    }
    
    setConsent(consentData);
    setConsentConfirmed(true);
    setShowConsent(false);
    console.log('✅ Consent confirmed and saved');
  };

  const unifiedPlanPrice = useMemo(() => {
    console.log('CheckoutPage - calculating price for finalPlanId:', finalPlanId, 'mentorCount:', mentorCount);
    console.log('CheckoutPage - selectedPlanId from state:', selectedPlanId);
    console.log('CheckoutPage - fallbackPlanId from localStorage:', fallbackPlanId);
    console.log('CheckoutPage - isSingleMentor:', isSingleMentor);
    // Student plans
    if (finalPlanId === 'base') return 199;
    if (finalPlanId === 'pro') return 399;
    if (finalPlanId === 'premium') return 749;
    
    // Mentor plans - different pricing for single mentor vs organization
    if (finalPlanId === 'mentor-basic') {
      if (isSingleMentor) {
        return 299; // Fixed price for single mentor basic
      } else {
        // Organization pricing: per mentor calculation
        const perMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
        return perMentor * mentorCount;
      }
    }
    if (finalPlanId === 'mentor-pro') {
      console.log('CheckoutPage - mentor-pro pricing: isSingleMentor:', isSingleMentor, 'mentorCount:', mentorCount);
      if (isSingleMentor) {
        console.log('CheckoutPage - returning 599 for single mentor pro');
        return 599; // Fixed price for single mentor pro
      } else {
        // Organization pricing: per mentor calculation
        const perMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
        console.log('CheckoutPage - returning organization pricing:', perMentor * mentorCount);
        return perMentor * mentorCount;
      }
    }
    if (finalPlanId === 'mentor-premium') {
      if (isSingleMentor) {
        return 999; // Fixed price for single mentor premium
      } else {
        // Organization pricing: per mentor calculation
        const perMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
        return perMentor * mentorCount;
      }
    }
    console.log('CheckoutPage - no price found for finalPlanId:', finalPlanId);
    return null;
  }, [finalPlanId, mentorCount, isSingleMentor]);

  const unifiedGst = useMemo(() => unifiedPlanPrice != null ? Number((unifiedPlanPrice * 0.18).toFixed(2)) : null, [unifiedPlanPrice]);
  const unifiedTotal = useMemo(() => unifiedPlanPrice != null ? Number((unifiedPlanPrice + (unifiedGst||0)).toFixed(2)) : null, [unifiedPlanPrice, unifiedGst]);

  const total = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + ((i.price || 0) * (i.quantity || 1)), 0);
    return printMode ? subtotal + 50 : subtotal; // Add ₹50 for print & delivery
  }, [items, printMode]);
  const canContinueAddress = useMemo(() => {
    const { name, email, phone, line1, city, state: st, postalCode } = address;
    return [name, email, phone, line1, city, st, postalCode].every(Boolean);
  }, [address]);

  const proceed = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const placeOrder = async () => {
    // Persist order to Firestore for admin tracking
    
    // Declare orderDoc outside try block so it's accessible in catch block
    let orderDoc = null;
    
    try {
      const user = auth?.currentUser || null;
      console.log('🔍 Debug - User state:', user ? 'Logged in' : 'Guest user');
      
      // Generate guest order ID for non-authenticated users
      let guestOrderId = null;
      if (!user) {
        guestOrderId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        // Store guest order ID in localStorage for future reference
        localStorage.setItem('guestOrderId', guestOrderId);
        console.log('🔍 Debug - Generated guest order ID:', guestOrderId);
      }
      
      orderDoc = {
        createdAt: new Date().toISOString(), // Use regular timestamp instead of serverTimestamp for testing
        userId: user?.uid || null,
        userEmail: user?.email || null,
        guestOrderId: guestOrderId, // Add guest order ID for tracking
        isGuestOrder: !user, // Flag to identify guest orders
        status: printMode ? 'in_queue_for_printing' : 'processing',
        printMode: !!printMode,
        address,
        items: items.map(i => ({
          id: i.id,
          title: i.title,
          image: i.image,
          price: i.price,
          quantity: i.quantity || 1,
          type: i.type || 'standard',
          isDailyActs: !!i.isDailyActs,
          scenes: i.scenes || null,
          description: i.description || ''
        })),
        fees: printMode ? { printAndDelivery: 50 } : { shipping: 0 },
        subtotal: items.reduce((s, i) => s + ((i.price || 0) * (i.quantity || 1)), 0),
        total,
        timeline: [
          { key: 'created', label: 'Order Created', at: new Date().toISOString() },
          ...(printMode ? [{ key: 'in_queue_for_printing', label: 'In queue for printing', at: new Date().toISOString() }] : [])
        ]
      };

      // For guest orders, also set userEmail to the address email for searchability
      if (!user && address?.email) {
        orderDoc.userEmail = address.email;
        console.log('🔍 Debug - Set guest order userEmail to address email:', address.email);
      }

      // Validate order data before sending to Firestore
      console.log('🔍 Debug - Order validation check:');
      console.log('🔍 Debug - Items count:', items.length);
      console.log('🔍 Debug - Address complete:', !!(address.name && address.email && address.phone));
      console.log('🔍 Debug - Total amount:', total);
      console.log('🔍 Debug - Guest order ID:', guestOrderId);
      
      if (!items.length) {
        throw new Error('No items in order');
      }
      if (!address.name || !address.email || !address.phone) {
        throw new Error('Incomplete address information');
      }
      if (!total || total <= 0) {
        throw new Error('Invalid total amount');
      }
      
      console.log('🔍 Debug - Attempting to create order document:', orderDoc);
      console.log('🔍 Debug - Firestore db object:', db);
      console.log('🔍 Debug - Collection reference:', collection(db, 'orders'));
      console.log('🔍 Debug - Firebase project ID:', db._databaseId?.projectId);
      console.log('🔍 Debug - Auth state:', auth?.currentUser ? 'Logged in as ' + auth.currentUser.email : 'Not logged in');

      const ref = await addDoc(collection(db, 'orders'), orderDoc);
      console.log('✅ Order created successfully:', ref.id, orderDoc);

      navigate('/order-confirmation', { 
        state: { 
          items, 
          total, 
          address, 
          etaDays: printMode ? 7 : 5, // Print orders take longer
          printMode,
          selectedBooks: state?.selectedBooks || [],
          orderId: ref.id
        } 
      });
    } catch (err) {
      console.error('❌ FAILED TO CREATE ORDER:', err);
      console.error('❌ Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack,
        orderData: orderDoc
      });
      
      // Show error to user
      alert(`❌ Failed to create order: ${err.message}\n\nPlease check the console for details.`);
      
      // Don't proceed to confirmation - let user try again
      return;
    }
  };

  const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  
  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    setItems((prev) => 
      prev.map((item) => 
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    );
  };
  
  const increaseQuantity = (id) => {
    updateQuantity(id, (items.find(item => item.id === id)?.quantity || 1) + 1);
  };
  
  const decreaseQuantity = (id) => {
    const currentQuantity = items.find(item => item.id === id)?.quantity || 1;
    if (currentQuantity > 1) {
      updateQuantity(id, currentQuantity - 1);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#FFF8E1] flex flex-col">
      <KidBackground variant="blobs" intensity={1} />
      {/* Soft gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(125%_100%_at_50%_0%,_#FFFCF8_0%,_#FFF6E8_42%,_#FFEFD6_100%)]" />
      {/* Decorative blurs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 h-72 w-72 rounded-full bg-[#F5E2C8]/25 blur-3xl" />
        <div className="absolute bottom-40 left-10 h-96 w-96 rounded-full bg-[#FFF0DC]/30 blur-3xl" />
      </div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
        <img src={splashImage} alt="" className="absolute top-10 right-5 opacity-15 w-[400px] h-[200px] object-cover rotate-12 mix-blend-multiply blur-[2px]" />
        <img src={splashImage} alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 w-[300px] h-[150px] object-cover rotate-[-10deg] mix-blend-multiply blur-[3px]" />
      </div>

      {/* Main content in a single window card - 2cm blank space on left and right only */}
      <div className="relative z-10 flex-1 flex justify-center pl-[2cm] pr-[2cm] py-4 md:py-8">
        <div className="w-full flex flex-col min-w-0">
          {/* Window card: nav inside + steps + content */}
          <div className="art-premium-surface flex-1 overflow-hidden rounded-2xl md:rounded-[32px] shadow-2xl">
            {/* Nav bar inside window */}
            <div className="border-b border-[#F5D99B]/55 px-4 sm:px-6 md:px-8">
              <ArtmosphereNavbar title="Artmosphere" subtitle="Complete your purchase" embedded />
            </div>
            <div className="p-4 sm:p-6 md:p-8">
              {/* Title + Back to Collection */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? 'Print Your Daily Acts Book' : 'Print Your Book') : 'Checkout'}
                </h1>
                <button
                  onClick={()=>navigate(printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? '/daily-activities' : '/my-art-stories') : '/create-books')}
                  className="art-btn-primary px-4 py-2.5 rounded-xl text-sm font-semibold shadow min-h-[44px] transition"
                >
                  {printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? 'Back to Daily Activities' : 'Back to My Stories') : 'Back to Artmosphere Collection'}
                </button>
              </div>
              {/* Step indicator - app style */}
              <div className="flex items-center gap-2 sm:gap-4 mb-6 md:mb-8 overflow-x-auto pb-2">
                {[1,2,3].map((n) => (
                  <div key={n} className="flex items-center gap-2 flex-shrink-0">
                    <div className={`art-step-dot w-8 h-8 sm:w-10 sm:h-10 text-sm ${
                      step > n ? 'art-step-dot--active' :
                      step === n ? 'art-step-dot--active' :
                      ''
                    }`}>{step > n ? '✓' : n}</div>
                    <span className={`text-sm font-medium whitespace-nowrap ${step >= n ? 'text-[#2f251b]' : 'text-gray-400'}`}>
                      {n === 1 ? (printMode ? 'Details' : 'Cart') : n === 2 ? 'Address' : (printMode ? 'Review' : 'Review')}
                    </span>
                    {n < 3 && <div className={`h-0.5 w-8 sm:w-12 rounded ${step > n ? 'art-step-line--active' : 'art-step-line'}`} />}
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-[#F5D99B]/55 bg-white/90 p-4 shadow-md sm:p-5 md:p-6">
          {/* Temporary Test Connection Button for Debugging */}
          {step === 3 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800 mb-2">🧪 Debug: Test Firestore Connection</div>
              <button
                onClick={async () => {
                  try {
                    console.log('🧪 Testing Firestore connection...');
                    console.log('🔍 Debug - Firebase project ID:', db._databaseId?.projectId);
                    console.log('🔍 Debug - Auth state:', auth?.currentUser ? 'Logged in as ' + auth.currentUser.email : 'Not logged in');
                    console.log('🔍 Debug - Firestore instance:', db);
                    console.log('🔍 Debug - Firestore app:', db._app);
                    console.log('🔍 Debug - Firestore config:', db._app?.options);
                    
                    // Test 1: Try to create a simple test document
                    console.log('🧪 Test 1: Creating simple test document...');
                    const simpleTestDoc = {
                      test: true,
                      timestamp: new Date().toISOString(),
                      message: 'Simple test document'
                    };
                    
                    const simpleRef = await addDoc(collection(db, 'orders'), simpleTestDoc);
                    console.log('✅ Test 1 SUCCESS: Simple document created with ID:', simpleRef.id);
                    
                    // Test 2: Try to create a document similar to guest order
                    console.log('🧪 Test 2: Creating guest order-like document...');
                    const guestOrderTestDoc = {
                      createdAt: new Date().toISOString(),
                      userId: null,
                      userEmail: 'test@example.com',
                      guestOrderId: 'test_guest_' + Date.now(),
                      isGuestOrder: true,
                      status: 'in_queue_for_printing',
                      test: true
                    };
                    
                    const guestRef = await addDoc(collection(db, 'orders'), guestOrderTestDoc);
                    console.log('✅ Test 2 SUCCESS: Guest order-like document created with ID:', guestRef.id);
                    
                    // Test 3: Try to create a document in a different collection
                    console.log('🧪 Test 3: Creating document in test_collection...');
                    const testCollectionDoc = {
                      test: true,
                      message: 'Testing different collection',
                      timestamp: new Date().toISOString()
                    };
                    
                    const testCollectionRef = await addDoc(collection(db, 'test_collection'), testCollectionDoc);
                    console.log('✅ Test 3 SUCCESS: Document created in test_collection with ID:', testCollectionRef.id);
                    
                    // Test 4: Try to read the documents back
                    console.log('🧪 Test 4: Testing read access...');
                    const simpleDoc = await getDoc(doc(db, 'orders', simpleRef.id));
                    const guestDoc = await getDoc(doc(db, 'orders', guestRef.id));
                    const testCollectionDocRead = await getDoc(doc(db, 'test_collection', testCollectionRef.id));
                    console.log('✅ Test 4 SUCCESS: Read access working');
                    
                    // Clean up test documents
                    console.log('🧹 Cleaning up test documents...');
                    await deleteDoc(doc(db, 'orders', simpleRef.id));
                    await deleteDoc(doc(db, 'orders', guestRef.id));
                    await deleteDoc(doc(db, 'test_collection', testCollectionRef.id));
                    console.log('✅ Test documents cleaned up');
                    
                    alert('✅ ALL TESTS PASSED! Firestore is working correctly.\n\nSimple doc: ' + simpleRef.id + '\nGuest doc: ' + guestRef.id);
                  } catch (error) {
                    console.error('❌ Firestore test failed:', error);
                    console.error('❌ Error details:', {
                      message: error.message,
                      code: error.code,
                      stack: error.stack
                    });
                    alert('❌ Firestore test failed: ' + error.message + '\n\nCheck console for details.');
                  }
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Test Firestore Connection
              </button>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? 'Your Daily Acts Book Details' : 'Your Book Details') : 'Your Cart'}
              </h2>
              <div>
                {items.length === 0 ? (
                  <div className="text-sm text-gray-600">No items selected.</div>
                ) : (
                  <div>
                    {items.map((i) => (
                      <div key={i.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 gap-3 border-b border-yellow-100 last:border-b-0">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <img src={i.image} alt={i.title} className="w-12 h-12 sm:w-14 sm:h-14 rounded object-cover border border-yellow-200 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 truncate">{i.title}</div>
                            <div className="text-xs text-gray-500">₹{i.price} each</div>
                            {printMode && i.isDailyActs && (
                              <div className="text-xs text-gray-500 mt-1">{i.scenes} activities completed</div>
                            )}
                            {printMode && i.author && (
                              <div className="text-xs text-gray-500 mt-1">By {i.author}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                          {/* Quantity Controls */}
                          {!printMode && (
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => decreaseQuantity(i.id)}
                                className="w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700 flex items-center justify-center text-sm font-semibold transition-colors"
                                disabled={i.quantity <= 1}
                              >
                                -
                              </button>
                              <span className="w-8 text-center text-sm font-semibold text-gray-900">{i.quantity}</span>
                              <button 
                                onClick={() => increaseQuantity(i.id)}
                                className="w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700 flex items-center justify-center text-sm font-semibold transition-colors"
                              >
                                +
                              </button>
                            </div>
                          )}
                          <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">₹{(i.price || 0) * (i.quantity || 1)}</div>
                          {!printMode && (
                            <button className="text-xs px-2 sm:px-3 py-1 rounded-full border border-yellow-200 text-yellow-800 hover:bg-yellow-50 whitespace-nowrap" onClick={()=>removeItem(i.id)}>Remove</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Totals - items only (no membership) */}
              <div className="mt-4 pt-4 border-t border-yellow-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Subtotal</div>
                  <div className="text-sm font-semibold text-gray-900">₹{printMode ? total - 50 : total}</div>
                </div>
                {printMode && (
                  <div className="mt-1 flex items-center justify-between">
                    <div className="text-sm text-gray-600">Print & Delivery</div>
                    <div className="text-sm text-gray-900">₹50</div>
                  </div>
                )}
                {!printMode && (
                  <div className="mt-1 flex items-center justify-between">
                    <div className="text-sm text-gray-600">Shipping</div>
                    <div className="text-sm text-gray-900">Free</div>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-base font-bold text-gray-900">Total</div>
                  <div className="text-base font-bold text-gray-900">₹{total}</div>
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {printMode ? 'Delivery Address' : 'Shipping Address'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Full Name" value={address.name} onChange={(e)=>setAddress({...address,name:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Email" type="email" value={address.email} onChange={(e)=>setAddress({...address,email:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Phone" value={address.phone} onChange={(e)=>setAddress({...address,phone:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Address line 1" value={address.line1} onChange={(e)=>setAddress({...address,line1:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px] sm:col-span-2" placeholder="Address line 2" value={address.line2} onChange={(e)=>setAddress({...address,line2:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="City" value={address.city} onChange={(e)=>setAddress({...address,city:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="State" value={address.state} onChange={(e)=>setAddress({...address,state:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Postal Code" value={address.postalCode} onChange={(e)=>setAddress({...address,postalCode:e.target.value})} />
                <input className="border border-yellow-200 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]" placeholder="Country" value={address.country} onChange={(e)=>setAddress({...address,country:e.target.value})} />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={agreed} onChange={(e)=>setAgreed(e.target.checked)} />
                I confirm my details are correct.
              </label>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? 'Review & Print Daily Acts Order' : 'Review & Print Order') : 'Review & Payment'}
              </h2>
              {/* Order Items - Mobile Optimized */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  {printMode ? 'Print Items' : 'Order Items'}
                </h3>
                <div className="rounded-lg border border-yellow-200 bg-white/70 divide-y divide-yellow-100">
                  {items.map((i)=> (
                    <div key={i.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-3 gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <img src={i.image} alt={i.title} className="w-12 h-12 sm:w-10 sm:h-10 rounded object-cover border border-yellow-200 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-900 font-medium truncate">{i.title}</div>
                          <div className="text-xs text-gray-600">Qty: {i.quantity} × ₹{i.price} each</div>
                          {printMode && i.author && (
                            <div className="text-xs text-gray-600">By {i.author}</div>
                          )}
                          {printMode && i.isDailyActs && (
                            <div className="text-xs text-gray-600">{i.scenes} activities completed</div>
                          )}
                          {printMode && i.scenes && !i.isDailyActs && (
                            <div className="text-xs text-gray-600">{i.scenes} scenes</div>
                          )}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 whitespace-nowrap sm:text-right">₹{(i.price || 0) * (i.quantity || 1)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address - Mobile Optimized */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  {printMode ? 'Delivery Address' : 'Shipping Address'}
                </h3>
                <div className="rounded-lg border border-yellow-200 bg-white/70 px-3 sm:px-4 py-3 text-sm text-gray-700">
                  <div className="font-medium text-gray-900 mb-1 break-words">{address.name}</div>
                  <div className="break-words">{address.line1}{address.line2 ? `, ${address.line2}`: ''}</div>
                  <div className="break-words">{address.city}, {address.state} {address.postalCode}</div>
                  <div className="break-words">{address.country}</div>
                  <div className="mt-2 text-gray-600 break-words">
                    <span className="font-medium text-gray-800">Contact:</span> 
                    <span className="block sm:inline sm:ml-1">{address.email}</span>
                    <span className="hidden sm:inline"> • </span>
                    <span className="block sm:inline">{address.phone}</span>
                  </div>
                </div>
              </div>

              {/* Totals */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  {printMode ? 'Print Order Summary' : 'Order Summary'}
                </h3>
                <div className="rounded-lg border border-yellow-200 bg-white/70 px-3 sm:px-4 py-3 text-sm">
                  {/* Item breakdown */}
                  {items.map((i) => (
                    <div key={i.id} className="flex items-center justify-between text-gray-700 mb-2 gap-2">
                      <div className="text-xs truncate flex-1 min-w-0">{i.title} × {i.quantity}</div>
                      <div className="text-xs whitespace-nowrap flex-shrink-0">₹{(i.price || 0) * (i.quantity || 1)}</div>
                    </div>
                  ))}
                  <div className="border-t border-yellow-200 pt-2 mt-2">
                  <div className="flex items-center justify-between text-gray-700">
                    <div>Subtotal</div>
                      <div className="font-medium text-gray-900">₹{total - (printMode ? 50 : 0)}</div>
                  </div>
                  <div className="flex items-center justify-between text-gray-700 mt-1">
                    <div>{printMode ? 'Print & Delivery' : 'Shipping'}</div>
                    <div className="text-gray-900">{printMode ? '₹50' : 'Free'}</div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-yellow-200">
                    <div className="text-base font-bold text-gray-900">Total</div>
                    <div className="text-base font-bold text-gray-900">₹{total}</div>
                    </div>
                  </div>
                </div>
                {/* Debug info - ALWAYS VISIBLE for testing */}
                <div className="mt-6 p-4 bg-red-100 border-2 border-red-300 rounded-lg text-sm font-bold">
                  <div className="text-red-800"><strong>🔍 DEBUG PANEL - Consent Form Testing</strong></div>
                  <div className="mt-2 text-red-700">
                    <div>isUnder13: <span className="bg-white px-2 py-1 rounded">{String(isUnder13)}</span></div>
                    <div>printMode: <span className="bg-white px-2 py-1 rounded">{String(printMode)}</span></div>
                    <div>Should show consent: <span className="bg-white px-2 py-1 rounded">{String(!printMode && isUnder13)}</span></div>
                    <div>localStorage signup_isUnder13: <span className="bg-white px-2 py-1 rounded">"{localStorage.getItem('signup_isUnder13')}"</span></div>
                    <div>localStorage signup_accountType: <span className="bg-white px-2 py-1 rounded">"{localStorage.getItem('signup_accountType')}"</span></div>
                    <div>localStorage signup_parentEmail: <span className="bg-white px-2 py-1 rounded">"{localStorage.getItem('signup_parentEmail')}"</span></div>
                  </div>
                  <div className="mt-3">
                    <button 
                      onClick={() => {
                        localStorage.setItem('signup_isUnder13', 'true');
                        localStorage.setItem('signup_accountType', 'student');
                        localStorage.setItem('signup_parentEmail', 'parent@test.com');
                        console.log('🔧 Test data set, reloading...');
                        window.location.reload();
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold mr-2"
                    >
                      🧪 Test: Set Under-13
                    </button>
                    <button 
                      onClick={() => {
                        localStorage.removeItem('signup_isUnder13');
                        localStorage.removeItem('signup_accountType');
                        localStorage.removeItem('signup_parentEmail');
                        console.log('🧹 Test data cleared, reloading...');
                        window.location.reload();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold"
                    >
                      🗑️ Clear Test Data
                    </button>
                  </div>
                </div>

                {/* Consent block for under-13 ONLY (payment gating) */}
                {!printMode && isUnder13 && (
                  <div className="art-callout-strip mt-6 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="auth-premium-callout-icon flex h-10 w-10 items-center justify-center rounded-full shadow-md">
                          <span className="text-[#8b6914] text-lg">🔒</span>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-amber-900">Consent & Privacy Settings</div>
                          <div className="text-sm text-[#6b5a48]">Parent approval required for children under 13.</div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowConsent(true)} 
                        className="px-6 py-3 rounded-xl text-sm font-semibold art-btn-primary transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        {consentConfirmed ? 'Review Settings' : 'Open Consent Form'}
                      </button>
                    </div>
                    {!consentConfirmed && (
                      <div className="flex items-center gap-3 text-sm text-red-700 p-4 bg-red-50/90 rounded-xl border-2 border-red-200/60">
                        <span className="w-6 h-6 bg-red-200 rounded-full flex items-center justify-center text-xs">⚠️</span>
                        <div>
                          <div className="font-semibold">Consent Required</div>
                          <div className="text-xs">Parent must confirm privacy settings before proceeding with payment.</div>
                        </div>
                      </div>
                    )}
                    {consentConfirmed && (
                      <div className="flex items-center gap-3 text-sm text-green-700 p-4 bg-green-50/90 rounded-xl border-2 border-green-200/60">
                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs">✅</span>
                        <div>
                          <div className="font-semibold">Consent Confirmed</div>
                          <div className="text-xs">Privacy settings have been approved by parent/guardian.</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Consent block for under-13 ONLY (payment gating) - ORIGINAL */}
                {false && !printMode && isUnder13 && (
                  <div className="art-callout-strip mt-6 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="auth-premium-callout-icon flex h-10 w-10 items-center justify-center rounded-full shadow-md">
                          <span className="text-[#8b6914] text-lg">🔒</span>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-amber-900">Consent & Privacy Settings</div>
                          <div className="text-sm text-[#6b5a48]">Parent approval required for children under 13.</div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowConsent(true)} 
                        className="px-6 py-3 rounded-xl text-sm font-semibold art-btn-primary transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        {consentConfirmed ? 'Review Settings' : 'Open Consent Form'}
                      </button>
                    </div>
                    {!consentConfirmed && (
                      <div className="flex items-center gap-3 text-sm text-red-700 p-4 bg-red-50/90 rounded-xl border-2 border-red-200/60">
                        <span className="w-6 h-6 bg-red-200 rounded-full flex items-center justify-center text-xs">⚠️</span>
                        <div>
                          <div className="font-semibold">Consent Required</div>
                          <div className="text-xs">Parent must confirm privacy settings before proceeding with payment.</div>
                        </div>
                      </div>
                    )}
                    {consentConfirmed && (
                      <div className="flex items-center gap-3 text-sm text-green-700 p-4 bg-green-50/90 rounded-xl border-2 border-green-200/60">
                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs">✅</span>
                        <div>
                          <div className="font-semibold">Consent Confirmed</div>
                          <div className="text-xs">Privacy settings have been approved by parent/guardian.</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between gap-3">
            <button onClick={back} disabled={step===1} className={`w-full sm:w-auto px-4 sm:px-5 py-3 rounded-xl text-sm font-medium border min-h-[44px] ${step===1?'border-gray-200 text-gray-400':'art-btn-secondary'}`}>Back</button>
            {step<3 ? (
              <button onClick={proceed} disabled={(step===2 && (!canContinueAddress || !agreed)) || items.length===0} className={`w-full sm:w-auto min-h-[44px] rounded-xl px-4 py-3 text-sm font-semibold sm:px-6 ${ (step===2 && (!canContinueAddress || !agreed)) || items.length===0 ? 'art-btn-secondary opacity-50' : 'art-btn-primary'}`}>{step===1?'Continue to Address':'Review Order'}</button>
            ) : (
              <button onClick={placeOrder} disabled={isUnder13 && !consentConfirmed} className={`w-full sm:w-auto px-4 sm:px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 min-h-[44px] ${isUnder13 && !consentConfirmed ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-800 hover:to-slate-700 text-white shadow-lg hover:shadow-xl'}`}>
                  {printMode ? (state?.selectedBooks?.[0]?.isDailyActs ? 'Place Daily Acts Print Order' : 'Place Print Order') : 'Place Order'}
              </button>
            )}
          </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Consent Modal for Under-13 Users */}
      {showConsent && (
        <SimpleConsentForm
          onAgree={handleConsentAgree}
          onCancel={() => setShowConsent(false)}
        />
      )}
    </div>
  );
}